import Reveal from "@/components/Reveal";
import { Link } from "react-router-dom";
import { ArrowRight, Facebook, MapPin, Truck, Heart, Phone, MessageSquare } from "lucide-react";
import { FACEBOOK_URL, PHONE_NUMBER, PHONE_TEL } from "@/data/site";

export default function Contact() {
    return (
        <>
            <section id="contact" data-testid="contact-section" className="py-16 md:py-24 lg:py-32 bg-[#FFFDF9]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <Reveal>
                        <div className="relative overflow-hidden rounded-[2.5rem] bg-[#2C180F] px-8 py-14 sm:px-14 sm:py-20 text-center">
                            <div className="pointer-events-none absolute -top-24 -left-24 h-72 w-72 rounded-full bg-[#C95B7D]/20 blur-3xl" />
                            <div className="pointer-events-none absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-[#D4AF37]/15 blur-3xl" />

                            <p className="font-script text-3xl sm:text-4xl text-[#F8D7E3]">Ready for something sweet?</p>
                            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#FFFDF9]">
                                Let's Make Your Day<br className="hidden sm:block" /> a Little Sweeter
                            </h2>
                            <p className="mx-auto mt-5 max-w-xl text-sm sm:text-base text-[#FFFDF9]/70 leading-relaxed">
                                The fastest way to reach us is the order request form — tell us your date and your dream
                                treats, and we'll take it from there.
                            </p>

                            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
                                <a
                                    href="#order"
                                    data-testid="contact-order-button"
                                    className="group inline-flex items-center gap-2 rounded-full bg-[#C95B7D] px-8 py-4 text-sm font-semibold text-white shadow-[0_12px_30px_rgba(201,91,125,0.45)] hover:bg-[#b44a6b] hover:-translate-y-0.5 transition-[transform,background-color] duration-300"
                                >
                                    Request an Order
                                    <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                                </a>
                                <a
                                    href="#menu"
                                    data-testid="contact-menu-button"
                                    className="rounded-full border border-[#FFFDF9]/25 px-8 py-4 text-sm font-semibold text-[#FFFDF9] hover:border-[#D4AF37]/70 hover:text-[#D4AF37] transition-[border-color,color] duration-300"
                                >
                                    View Our Menu
                                </a>
                                <a
                                    href={`tel:${PHONE_TEL}`}
                                    data-testid="contact-call-button"
                                    className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/60 bg-[#D4AF37]/10 px-8 py-4 text-sm font-semibold text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#2C180F] transition-[background-color,color] duration-300"
                                >
                                    <Phone className="h-4 w-4" />
                                    Call {PHONE_NUMBER}
                                </a>
                                <a
                                    href={`sms:${PHONE_TEL}?&body=${encodeURIComponent("Hi Annabell! I'd like to ask about an order.")}`}
                                    data-testid="contact-text-button"
                                    className="inline-flex items-center gap-2 rounded-full border border-[#D4AF37]/60 bg-[#D4AF37]/10 px-8 py-4 text-sm font-semibold text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#2C180F] transition-[background-color,color] duration-300"
                                >
                                    <MessageSquare className="h-4 w-4" />
                                    Text Us
                                </a>
                                <a
                                    href={FACEBOOK_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-facebook-button"
                                    className="inline-flex items-center gap-2 rounded-full border border-[#FFFDF9]/25 px-8 py-4 text-sm font-semibold text-[#FFFDF9] hover:border-[#D4AF37]/70 hover:text-[#D4AF37] transition-[border-color,color] duration-300"
                                >
                                    <Facebook className="h-4 w-4" />
                                    Find Us on Facebook
                                </a>
                            </div>

                            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-xs text-[#FFFDF9]/60" data-testid="contact-details">
                                <a href={`tel:${PHONE_TEL}`} className="flex items-center gap-2 hover:text-[#F8D7E3] transition-colors" data-testid="contact-phone">
                                    <Phone className="h-4 w-4 text-[#D4AF37]" />
                                    {PHONE_NUMBER}
                                </a>
                                <span className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-[#D4AF37]" />
                                    Shelton · Olympia · Lacey, WA
                                </span>
                                <span className="flex items-center gap-2">
                                    <Truck className="h-4 w-4 text-[#D4AF37]" />
                                    Pickup &amp; delivery (small fee)
                                </span>
                                <span className="flex items-center gap-2">
                                    <Heart className="h-4 w-4 text-[#D4AF37]" />
                                    Homemade · Fresh · Made With Love
                                </span>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            <footer data-testid="footer" className="bg-[#2C180F] border-t border-[#FFFDF9]/10 py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                        <a href="#home" className="flex items-center gap-3" data-testid="footer-logo">
                            <img src="/annabells-logo.png" alt="Annabells Cupcakes and More logo" className="h-14 w-14 rounded-full object-cover ring-2 ring-[#D4AF37]/50" />
                            <span className="leading-none">
                                <span className="font-script text-2xl text-[#F8D7E3] block">Annabells</span>
                                <span className="text-[10px] tracking-[0.3em] uppercase text-[#FFFDF9]/50 font-semibold">Cupcakes &amp; More</span>
                            </span>
                        </a>

                        <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-[#FFFDF9]/60" data-testid="footer-links">
                            <a href="#about" className="hover:text-[#F8D7E3] transition-colors" data-testid="footer-link-about">About</a>
                            <a href="#menu" className="hover:text-[#F8D7E3] transition-colors" data-testid="footer-link-menu">Menu</a>
                            <a href="#treats" className="hover:text-[#F8D7E3] transition-colors" data-testid="footer-link-treats">Treats</a>
                            <a href="#gallery" className="hover:text-[#F8D7E3] transition-colors" data-testid="footer-link-gallery">Gallery</a>
                            <a href="#order" className="hover:text-[#F8D7E3] transition-colors" data-testid="footer-link-order">Order</a>
                            <a href="#faq" className="hover:text-[#F8D7E3] transition-colors" data-testid="footer-link-faq">FAQ</a>
                        </nav>

                        <div className="flex items-center gap-3">
                            <a
                                href={FACEBOOK_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Annabells Cupcakes and More on Facebook"
                                data-testid="footer-facebook"
                                className="inline-flex h-10 items-center gap-2 rounded-full border border-[#FFFDF9]/15 px-4 text-xs font-semibold text-[#FFFDF9]/70 hover:border-[#C95B7D] hover:text-[#F8D7E3] transition-[border-color,color] duration-300"
                            >
                                <Facebook className="h-4 w-4" />
                                Facebook
                            </a>
                        </div>
                    </div>
                    <p className="mt-10 text-center text-xs text-[#FFFDF9]/40" data-testid="footer-copy">
                        © {new Date().getFullYear()} Annabells Cupcakes and More · Homemade with love in Washington
                        <span className="mx-2 opacity-50">·</span>
                        <Link to="/admin" data-testid="footer-owner-login" className="hover:text-[#F8D7E3] transition-colors">Owner login</Link>
                    </p>
                </div>
            </footer>
        </>
    );
}
